/**
 * Fournisseurs de données et d'authentification d'Atelier Augmenté (19/10/2026).
 * Données : toute la base chargée depuis /api/atomic/db, moteur d'Atomic en mémoire, écritures
 * répercutées sur le serveur. Authentification : session du CRM (lien magique, cookie partagé
 * avec dashboard.atelieraugmente.fr) ; pas de connexion propre à Atomic.
 */
import type { AuthProvider, Identifier } from "ra-core";
import { canAccess } from "../commons/canAccess";
import { createDataProvider, type Persistance } from "../fakerest/dataProvider";
import type { CrmDataProvider } from "../types";

export type Moi = { id: number; email: string; nom: string | null; role: "admin" | "commercial" };

async function json(url: string, init?: RequestInit) {
  const r = await fetch(url, { credentials: "same-origin", headers: { "content-type": "application/json" }, ...init });
  if (r.status === 401) { window.location.href = "/connexion"; throw new Error("Connexion requise"); }
  const d = await r.json().catch(() => null);
  if (!r.ok) throw new Error(d?.erreur || `Erreur ${r.status}`);
  return d;
}

export async function chargerMoi(): Promise<Moi | null> {
  const r = await fetch("/api/auth/moi", { credentials: "same-origin" });
  if (!r.ok) return null;
  return (await r.json())?.utilisateur || null;
}

const persistance: Persistance = {
  creer: (resource, data) => json(`/api/atomic/${resource}`, { method: "POST", body: JSON.stringify(data) }),
  remplacer: (resource, id: Identifier, data) => json(`/api/atomic/${resource}/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  supprimer: async (resource, id: Identifier) => { await json(`/api/atomic/${resource}/${id}`, { method: "DELETE" }); },
};

export async function creerFournisseurs(moi: Moi): Promise<{ dataProvider: CrmDataProvider; authProvider: AuthProvider }> {
  const db = await json("/api/atomic/db");
  if (!db.configuration?.length) db.configuration = [];
  const identite = {
    id: moi.id,
    fullName: moi.nom || moi.email,
  };
  const authProvider: AuthProvider = {
    login: async () => undefined,
    logout: async () => {
      await fetch("/api/auth/deconnexion", { method: "POST", credentials: "same-origin" }).catch(() => {});
      window.location.href = "/connexion";
    },
    checkError: async (e: any) => { if (e?.status === 401) throw e; },
    checkAuth: async () => undefined,
    canAccess: async ({ signal: _s, ...params }: any) => canAccess(moi.role === "admin" ? "admin" : "user", params),
    getIdentity: async () => identite,
  };
  const dataProvider = createDataProvider({ db, latency: 0, silent: true, persist: persistance, authProvider: { getIdentity: async () => identite } as any });
  return { dataProvider, authProvider };
}

/** Version des données, pour proposer d'actualiser quand quelqu'un d'autre a écrit. */
export const versionDonnees = async (): Promise<string> => (await json("/api/atomic/version")).version;
